/* eslint-disable @typescript-eslint/ban-types */
import { COMMERCETOOLS_API_URL } from "@lib/constants/urlConstants";
import type { DocumentNode } from "graphql";
import { initUrqlClient } from "next-urql";
import fetch from "../helpers/fetchWithLogging";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import type { Client as UrqlClient } from "urql";

/**
 * This is a GraphQL client wrapper used for all GraphQL queries.
 */

const GRAPHQL_ENDPOINTS = {
    [ServiceType.COMMERCE_TOOLS]: `${COMMERCETOOLS_API_URL}/${process.env.NEXT_PUBLIC_COMMERCETOOLS_PROJECT_KEY}/graphql`,
};

let client: UrqlClient;
const getClient = (service: ServiceType) => {
    if (!client) {
        const graphQlEndpoint = GRAPHQL_ENDPOINTS[service];
        if (!graphQlEndpoint) {
            throw new Error(`No GraphQL endpoint found for service ${service}`);
        }

        client = initUrqlClient(
            {
                url: GRAPHQL_ENDPOINTS[service],
                fetch,
            },
            false
        );
    }
    return client;
};

class GraphQlClientWrapper {
    private async query<Data = any, Variables extends object = {}>(
        query: string | DocumentNode,
        serviceEndpoint: ServiceType,
        variables?: Variables
    ) {
        const startTime = performance.now();
        const result = await getClient(serviceEndpoint)
            .query<Data, Variables>(query, variables)
            .toPromise();

        Logger.info(serviceEndpoint, `${serviceEndpoint} Query Time`, {
            tag: LogTag.PERFORMANCE,
            duration: performance.now() - startTime,
        });

        if (result.error) {
            Logger.error(serviceEndpoint, `${serviceEndpoint} Query Error`, {
                tag: LogTag.ERROR,
                error: result.error,
            });
        }

        return result;
    }

    async queryCommerceTools<Data = any, Variables extends object = {}>(
        query: string | DocumentNode,
        variables?: Variables
    ) {
        return this.query<Data, Variables>(query, ServiceType.COMMERCE_TOOLS, variables);
    }
}

const graphQlClient = new GraphQlClientWrapper();

export { graphQlClient };
